/**
 * ObsidianPageList — vibe-designer 1.173.0 Phase 5β1.
 *
 * Per-service viz on the OBSIDIAN detail panel. Calls
 * GET /api/knowledge/services/obsidian/page-list → renders {path, size, mtime}
 * for each markdown file under /space. Mounted ABOVE the existing search input
 * in KnowledgeServiceDetail when the active service is `obsidian`.
 */
import { useEffect } from 'react';
import { Loader2, RefreshCw, FileText } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import type { ObsidianPage } from '@/services/knowledgeApi';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// R2 #3: intentionally UTC (ISO 8601 YYYY-MM-DD HH:MM:SS). Local-timezone
// display is deferred to Phase 5β2 once we have a Settings panel where the
// user can opt-in. UTC is unambiguous + matches the operator's tmux/SSH
// terminal output where mtime is also UTC.
function formatMtime(epoch: number): string {
  const d = new Date(epoch * 1000);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

export function ObsidianPageList() {
  const obsidianPages = useKnowledgeStore((s) => s.obsidianPages);
  const isFetching = useKnowledgeStore((s) => s.isFetchingObsidianPages);
  const error = useKnowledgeStore((s) => s.obsidianPagesError);
  const fetchObsidianPageList = useKnowledgeStore((s) => s.fetchObsidianPageList);
  // 1.191.0 Phase 7.6.1 — re-fetch on SSE ingest event.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);

  useEffect(() => {
    void fetchObsidianPageList();
  }, [fetchObsidianPageList, lastIngestAt]);

  return (
    <section className="rounded border border-vibe-border bg-vibe-bg-secondary p-3 mb-4">
      <header className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary">
          Pages on /space
          {obsidianPages && (
            <span className="ml-2 text-vibe-text-secondary tabular-nums">
              {obsidianPages.total} page{obsidianPages.total === 1 ? '' : 's'}
            </span>
          )}
        </h2>
        <button
          data-testid="obsidian-page-list-refresh"
          type="button"
          onClick={() => void fetchObsidianPageList()}
          disabled={isFetching}
          className="px-2 py-1 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50 flex items-center gap-1"
        >
          {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          <span>Refresh</span>
        </button>
      </header>

      {error && (
        <div
          data-testid="obsidian-page-list-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
        >
          {error.message}
        </div>
      )}

      {!error && obsidianPages && obsidianPages.error && (
        <div
          data-testid="obsidian-page-list-error"
          className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
        >
          {obsidianPages.error}
        </div>
      )}

      {!error && obsidianPages && !obsidianPages.error && obsidianPages.pages.length === 0 && (
        <div
          data-testid="obsidian-page-list-empty"
          className="text-xs text-vibe-text-secondary py-2"
        >
          No pages found under /space.
        </div>
      )}

      {obsidianPages && obsidianPages.pages.length > 0 && (
        <ul className="space-y-0.5">
          {obsidianPages.pages.map((p) => (
            <PageRow key={p.path} page={p} />
          ))}
        </ul>
      )}
    </section>
  );
}

function PageRow({ page }: { page: ObsidianPage }) {
  return (
    <li
      data-testid="obsidian-page-row"
      className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-vibe-bg"
    >
      <FileText className="w-3 h-3 text-vibe-text-secondary" />
      <span className="font-mono text-vibe-text truncate flex-1" title={page.path}>
        {page.path}
      </span>
      <span className="text-vibe-text-secondary tabular-nums">
        {formatSize(page.size)}
      </span>
      <span className="text-vibe-text-secondary tabular-nums">
        {formatMtime(page.mtime_epoch)}
      </span>
    </li>
  );
}
